import z from 'zod'
import {
  firestoreWriteTimestamp,
  nullSafeFirestoreTimestamp,
  nullSafeOptionalBoolean,
  nullSafeOptionalFirestoreTimestamp,
  nullSafeOptionalNumber,
  nullSafeOptionalString,
} from '../shared/schema'
import {
  AssignmentGroupingType,
  AssignmentState,
  AssignmentType,
} from './types'

export { AssignmentGroupingType, AssignmentState, AssignmentType }

const ltiGradingLineItemOptions = z.object({
  id: z.string(),
  label: z.string(),
  scoreMaximum: z.number(),
})

// this is the dart definition
// required int assignedAt,
// required AssignmentType assignmentType,
// required int expiresAt,
// required AssignmentGroupingType groupingType, default: AssignmentGroupingType.none
// required String sectionId,
// required String slideDeckId,
// int? groupingSize,
// int? updatedAt,
export const schema = z.object({
  assignedAt: nullSafeOptionalFirestoreTimestamp(),
  assignmentType: z.nativeEnum(AssignmentType),
  assignmentGradingScalars: z
    .object({
      attendanceScore: z.number().optional().default(0),
      quizScore: z.number().optional().default(0),
      participationScore: z.number().optional().default(0),
      totalRubricScore: z.number().optional().default(0),
    })
    .optional(),
  assignmentOverdueAt: nullSafeOptionalFirestoreTimestamp(),
  customGroupingSelected: nullSafeOptionalBoolean(false),
  expiresAt: nullSafeOptionalFirestoreTimestamp(),
  forbidStudentGroupCreation: nullSafeOptionalBoolean(false),
  forbidStudentGroupScheduling: nullSafeOptionalBoolean(false),
  forbidStudentGroupSelection: nullSafeOptionalBoolean(false),
  forceScheduledAt: nullSafeOptionalFirestoreTimestamp(),
  groupingType: z.preprocess((val) => {
    return val ?? AssignmentGroupingType.manual
  }, z.nativeEnum(AssignmentGroupingType)),
  assignmentState: z.preprocess((val) => {
    return val ?? AssignmentState.active // default to active
  }, z.nativeEnum(AssignmentState)),
  sectionId: z.string(),
  slideDeckId: z.string(),
  /** @deprecated use the desiredGroupSize getter instead */
  groupingSize: nullSafeOptionalNumber(),
  /** @deprecated use the maxGroupSize getter instead */
  groupingSizeMaximum: nullSafeOptionalNumber(),
  groupingSizeMinimum: nullSafeOptionalNumber(),
  catalogId: z.string().nullish(),
  updatedAt: nullSafeFirestoreTimestamp(),
  ltiMaxScore: z.number().optional(),
  ltiCourseId: z.string().optional(),
  ltiAssignmentId: z.string().optional(),
  ltiDomain: z.string().optional(),
  ltiLineItemMissing: z.boolean().optional(),
  peerReviewExcludesSelfReview: nullSafeOptionalBoolean(false),
  peerReviewsHiddenFromReviewees: nullSafeOptionalBoolean(false),
  ltiGradingLineItemId: nullSafeOptionalString(),
  ltiGradingLineItemOptions: z.array(ltiGradingLineItemOptions).optional(),
  /** allow participants to view final score calculation in meeting results view */
  showFinalGradeToParticipants: nullSafeOptionalBoolean(false),
})

export const writeSchema = schema.extend({
  assignedAt: firestoreWriteTimestamp,
  assignmentOverdueAt: firestoreWriteTimestamp.optional(),
  expiresAt: firestoreWriteTimestamp,
  updatedAt: firestoreWriteTimestamp,
})

export const draftWriteSchema = writeSchema.extend({
  assignedAt: firestoreWriteTimestamp.optional(),
  assignmentOverdueAt: firestoreWriteTimestamp.optional(),
  expiresAt: firestoreWriteTimestamp.optional(),
})

export const empty = Object.freeze(
  schema.parse({
    assignedAt: { seconds: 0, nanoseconds: 0 },
    assignmentType: AssignmentType.ManyGroups,
    expiresAt: { seconds: 0, nanoseconds: 0 },
    groupingType: AssignmentGroupingType.manual,
    sectionId: '',
    slideDeckId: '',
  })
)

type FirestoreSectionAssignmentInferredType = z.infer<typeof schema>

export type FirestoreSectionAssignmentWrite = z.infer<typeof writeSchema>

export interface FirestoreSectionAssignment extends FirestoreSectionAssignmentInferredType {}
